import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { customerCreditNotesAPI, customersAPI, offersAPI, freeOffersAPI, invoicesAPI, freeInvoicesAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, FileText, CheckCircle, Ban, Loader2, Users, Handshake } from "lucide-react";

const STATUS = {
  entwurf: { label: "Entwurf", variant: "secondary" },
  finalisiert: { label: "Finalisiert", variant: "default" },
  verrechnet: { label: "Verrechnet", variant: "success" },
  storniert: { label: "Storniert", variant: "destructive" },
};

const fmtEur = (v) =>
  Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const emptyItem = () => ({ description: "", quantity: 1, unit: "Stück", unit_price: 0 });

export default function CustomerCreditNotesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [freeInvoices, setFreeInvoices] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [form, setForm] = useState({
    customer_id: "",
    reference_type: "offer", // 'offer' | 'invoice'
    offer_id: "",
    source_invoice_id: "",
    reason: "",
    items: [emptyItem()],
    include_vat: true,
    vat_rate: 19,
    notes: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [cnRes, custRes, offRes, foffRes, invRes, freeInvRes] = await Promise.all([
          customerCreditNotesAPI.list(),
          customersAPI.list(),
          offersAPI.list().catch(() => ({ data: [] })),
          freeOffersAPI.list().catch(() => ({ data: [] })),
          invoicesAPI.list().catch(() => ({ data: [] })),
          freeInvoicesAPI.list().catch(() => ({ data: [] })),
        ]);
        setRows(cnRes.data || []);
        setCustomers(custRes.data || []);
        // PV-, Freitext- und Legacy-Angebote in einem Array zur Auswahl
        const allOffers = [
          ...(offRes.data || []).map(o => ({ ...o, _kind: "PV" })),
          ...(foffRes.data || []).map(o => ({ ...o, _kind: "Freitext" })),
        ];
        setOffers(allOffers);
        setInvoices(invRes.data || []);
        setFreeInvoices(freeInvRes.data || []);
      } catch (e) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const reload = async () => {
    const res = await customerCreditNotesAPI.list();
    setRows(res.data || []);
  };

  const resetForm = () => {
    setForm({
      customer_id: "",
      reference_type: "offer",
      offer_id: "",
      source_invoice_id: "",
      reason: "",
      items: [emptyItem()],
      include_vat: true,
      vat_rate: 19,
      notes: "",
    });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEdit = async (row) => {
    setEditing(row);
    setForm({
      customer_id: row.customer_id,
      reference_type: row.source_invoice_id ? "invoice" : "offer",
      offer_id: row.offer_id || "",
      source_invoice_id: row.source_invoice_id || "",
      reason: row.reason || "",
      items: row.items?.length ? row.items : [emptyItem()],
      include_vat: row.include_vat !== false,
      vat_rate: row.vat_rate || 19,
      notes: row.notes || "",
    });
    setShowDialog(true);
  };

  const totalGross = useMemo(
    () =>
      form.items.reduce(
        (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
        0
      ),
    [form.items]
  );

  const handleSave = async () => {
    if (!form.customer_id) return toast.error("Bitte Kunden auswählen");
    if (form.reference_type === "offer" && !form.offer_id)
      return toast.error("Bitte Angebot wählen");
    if (form.reference_type === "invoice" && !form.source_invoice_id)
      return toast.error("Bitte Rechnung wählen");
    if (!form.reason.trim()) return toast.error("Grund ist Pflicht");
    if (!form.items.length || form.items.every(i => !i.description || !i.unit_price))
      return toast.error("Mindestens eine gefüllte Position");

    const payload = {
      customer_id: form.customer_id,
      offer_id: form.reference_type === "offer" ? form.offer_id : null,
      source_invoice_id: form.reference_type === "invoice" ? form.source_invoice_id : null,
      reason: form.reason.trim(),
      items: form.items
        .filter(i => i.description && parseFloat(i.unit_price) > 0)
        .map(i => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 1,
          unit: i.unit || "Stück",
          unit_price: parseFloat(i.unit_price) || 0,
        })),
      include_vat: !!form.include_vat,
      vat_rate: parseFloat(form.vat_rate) || 0,
      notes: form.notes || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await customerCreditNotesAPI.update(editing.id, {
          reason: payload.reason,
          items: payload.items,
          include_vat: payload.include_vat,
          vat_rate: payload.vat_rate,
          notes: payload.notes,
        });
        toast.success("Gutschrift aktualisiert");
      } else {
        await customerCreditNotesAPI.create(payload);
        toast.success("Gutschrift erstellt");
      }
      setShowDialog(false);
      resetForm();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async (row) => {
    if (!window.confirm(`Gutschrift finalisieren? Sie erhält eine feste Nummer und ist danach unveränderlich.`)) return;
    setActionId(row.id);
    try {
      const res = await customerCreditNotesAPI.finalize(row.id);
      toast.success(`Finalisiert: ${res.data.credit_note_number}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Finalisieren");
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (row) => {
    if (!window.confirm("Gutschrift wirklich stornieren?")) return;
    setActionId(row.id);
    try {
      await customerCreditNotesAPI.cancel(row.id);
      toast.success("Storniert");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Stornieren");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Entwurf löschen?")) return;
    setActionId(row.id);
    try {
      await customerCreditNotesAPI.delete(row.id);
      toast.success("Gelöscht");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setActionId(null);
    }
  };

  const handleDownloadPdf = async (row) => {
    setActionId(row.id);
    try {
      const res = await customerCreditNotesAPI.downloadPdf(row.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Gutschrift_${row.credit_note_number || "Entwurf"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF konnte nicht geladen werden");
    } finally {
      setActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Kunden-Gutschriften</h1>
          <p className="text-muted-foreground">Positions-Gutschriften an Ihre Kunden – Nummernkreis GU-YYYY-NNNN.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="tab-supplier-credit-notes">
            <Link to="/gutschriften">
              <Handshake className="h-4 w-4 mr-2" /> Vertriebs-Gutschriften
            </Link>
          </Button>
          <Button onClick={openCreate} data-testid="create-customer-credit-note-btn">
            <Plus className="h-4 w-4 mr-2" /> Neue Kunden-Gutschrift
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Alle Kunden-Gutschriften ({rows.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center">Noch keine Kunden-Gutschriften vorhanden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-credit-notes-table">
                <thead className="text-left border-b">
                  <tr>
                    <th className="py-2 px-2">Nummer</th>
                    <th className="py-2 px-2">Kunde</th>
                    <th className="py-2 px-2">Referenz</th>
                    <th className="py-2 px-2">Grund</th>
                    <th className="py-2 px-2 text-right">Brutto</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const st = STATUS[r.status] || { label: r.status, variant: "secondary" };
                    return (
                      <tr key={r.id} className="border-b hover:bg-muted/40">
                        <td className="py-2 px-2 font-mono">{r.credit_note_number || <span className="italic text-muted-foreground">Entwurf</span>}</td>
                        <td className="py-2 px-2">{r.customer_name}</td>
                        <td className="py-2 px-2 text-xs">
                          {r.offer_number && <div>Angebot {r.offer_number}</div>}
                          {r.source_invoice_number && <div>Rechnung {r.source_invoice_number}</div>}
                        </td>
                        <td className="py-2 px-2 max-w-xs truncate" title={r.reason}>{r.reason}</td>
                        <td className="py-2 px-2 text-right font-medium tabular-nums">-{fmtEur(r.gross_amount)}</td>
                        <td className="py-2 px-2"><Badge variant={st.variant}>{st.label}</Badge></td>
                        <td className="py-2 px-2 text-right space-x-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(r)} disabled={actionId === r.id} data-testid={`pdf-${r.id}`}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          {r.status === "entwurf" && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Bearbeiten</Button>
                              <Button variant="ghost" size="sm" onClick={() => handleFinalize(r)} disabled={actionId === r.id} data-testid={`finalize-${r.id}`}>
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(r)} disabled={actionId === r.id}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {r.status === "finalisiert" && (
                            <Button variant="ghost" size="sm" onClick={() => handleCancel(r)} disabled={actionId === r.id} data-testid={`cancel-${r.id}`}>
                              <Ban className="h-4 w-4 text-orange-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Kunden-Gutschrift bearbeiten" : "Neue Kunden-Gutschrift"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Kunde + Referenz */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Kunde *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="ccn-customer-select"><SelectValue placeholder="Kunden wählen..." /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}{c.company_name ? ` (${c.company_name})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Bezug</Label>
                <Select value={form.reference_type} onValueChange={(v) => setForm({ ...form, reference_type: v, offer_id: "", source_invoice_id: "" })} disabled={!!editing}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offer">Angebot</SelectItem>
                    <SelectItem value="invoice">Rechnung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.reference_type === "offer" ? (
              <div className="space-y-1">
                <Label>Angebot *</Label>
                {(() => {
                  const forCust = offers.filter(o => o.customer_id === form.customer_id);
                  const opts = (form.customer_id && forCust.length > 0) ? forCust : offers;
                  return (
                    <>
                      <Select value={form.offer_id} onValueChange={(v) => setForm({ ...form, offer_id: v })} disabled={!!editing}>
                        <SelectTrigger data-testid="ccn-offer-select"><SelectValue placeholder="Angebot wählen..." /></SelectTrigger>
                        <SelectContent>
                          {opts.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine Angebote gefunden</div>
                          ) : opts.map(o => (
                            <SelectItem key={o.id} value={o.id}>
                              [{o._kind}] {o.offer_number} — {fmtEur(o.total_price ?? o.display_price ?? o.total ?? 0)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.customer_id && forCust.length === 0 && offers.length > 0 && (
                        <p className="text-xs text-amber-600">
                          Für diesen Kunden ist kein Angebot direkt zugeordnet – es werden alle Angebote angezeigt.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Rechnung *</Label>
                {(() => {
                  // Reguläre + Freitext-Rechnungen zusammenführen
                  const merged = [
                    ...(invoices || []).map(i => ({
                      id: i.id,
                      customer_id: i.customer_id,
                      number: i.invoice_number || "(Entwurf)",
                      amount: i.gross_total ?? i.total ?? 0,
                      kind: "PV/Projekt",
                      status: i.status || "",
                    })),
                    ...(freeInvoices || []).map(i => ({
                      id: i.id,
                      customer_id: i.customer_id,
                      number: i.invoice_number || "(Entwurf)",
                      amount: i.total ?? i.gross_total ?? 0,
                      kind: "Freitext",
                      status: i.status || "",
                    })),
                  ];
                  // Wenn der ausgewählte Kunde in der Liste vorkommt: nur seine.
                  // Ansonsten fallbackweise alle zeigen (falls customer_id-Mapping fehlt).
                  const forCustomer = merged.filter(i => i.customer_id === form.customer_id);
                  const options = (form.customer_id && forCustomer.length > 0) ? forCustomer : merged;
                  return (
                    <>
                      <Select
                        value={form.source_invoice_id}
                        onValueChange={(v) => setForm({ ...form, source_invoice_id: v })}
                        disabled={!!editing}
                      >
                        <SelectTrigger data-testid="ccn-invoice-select"><SelectValue placeholder="Rechnung wählen..." /></SelectTrigger>
                        <SelectContent>
                          {options.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine Rechnungen gefunden</div>
                          ) : options.map(i => (
                            <SelectItem key={i.id} value={i.id}>
                              [{i.kind}] {i.number} — {fmtEur(i.amount)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.customer_id && forCustomer.length === 0 && merged.length > 0 && (
                        <p className="text-xs text-amber-600">
                          Für diesen Kunden ist keine Rechnung direkt zugeordnet – es werden alle Rechnungen angezeigt.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            <div className="space-y-1">
              <Label>Grund *</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="z.B. Nachträglicher Nachlass auf Wallbox"
                data-testid="ccn-reason-input"
              />
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50">
              <Switch checked={!!form.include_vat} onCheckedChange={(v) => setForm({ ...form, include_vat: v })} />
              <Label className="cursor-pointer">Mit MwSt.</Label>
              {form.include_vat && (
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.vat_rate}
                  onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                  className="w-24 h-8"
                />
              )}
              <span className="text-xs text-muted-foreground ml-auto">MwSt-Behandlung wie beim Ursprungsangebot.</span>
            </div>

            {/* Positionen */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Positionen</Label>
                <Button variant="outline" size="sm" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>
                  <Plus className="h-4 w-4 mr-1" /> Position
                </Button>
              </div>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 p-2 border rounded-lg">
                  <Input
                    className="col-span-6 h-8"
                    placeholder="Beschreibung"
                    value={it.description}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx] = { ...items[idx], description: e.target.value };
                      setForm({ ...form, items });
                    }}
                    data-testid={`ccn-item-desc-${idx}`}
                  />
                  <Input
                    className="col-span-2 h-8"
                    type="number"
                    step="0.01"
                    placeholder="Menge"
                    value={it.quantity}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx] = { ...items[idx], quantity: e.target.value };
                      setForm({ ...form, items });
                    }}
                  />
                  <Input
                    className="col-span-3 h-8"
                    type="number"
                    step="0.01"
                    placeholder="Einzelpreis"
                    value={it.unit_price}
                    onChange={(e) => {
                      const items = [...form.items];
                      items[idx] = { ...items[idx], unit_price: e.target.value };
                      setForm({ ...form, items });
                    }}
                    data-testid={`ccn-item-price-${idx}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="col-span-1 h-8 p-0 text-red-600"
                    onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}
                    disabled={form.items.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="text-right text-sm text-muted-foreground">
                Summe (Brutto): <span className="font-bold text-lg text-red-600">-{fmtEur(totalGross)}</span>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Anmerkungen (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="ccn-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? "Speichern" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
